import { Row } from '@tanstack/react-table';
import { Link as LinkIcon } from 'lucide-react';
import { useMemo, useRef } from 'react';

import { useEnvironmentList } from '@/react/portainer/environments/queries';
import { useGroups } from '@/react/portainer/environments/environment-groups/queries';
import { EnvironmentInfo } from '@/react/portainer/policies/types';

import { ExpandableDatatable } from '@@/datatables/ExpandableDatatable';
import { NestedDatatable } from '@@/datatables/NestedDatatable';
import { useTableState } from '@@/datatables/useTableState';
import {
  createPersistedStore,
  refreshableSettings,
  hiddenColumnsSettings,
} from '@@/datatables/types';
import { Alert } from '@@/Alert';
import { mergeOptions } from '@@/datatables/extend-options/mergeOptions';
import { defaultGlobalFilterFn } from '@@/datatables/Datatable';
import { withGlobalFilter } from '@@/datatables/extend-options/withGlobalFilter';

import { usePolicy } from '../../queries/usePolicy';
import { type TableSettings } from '../types';

import { getGroupColumns, getEnvironmentColumns } from './columns';
import { AttachmentRowData } from './types';

interface Props {
  policyId: number;
}

const storageKey = 'policy-attachments-datatable';
const settingsStore = createPersistedStore<TableSettings>(
  storageKey,
  'name',
  (set) => ({
    ...refreshableSettings(set),
    ...hiddenColumnsSettings(set),
    expanded: true,
    setExpanded(value) {
      set({ expanded: value });
    },
  })
);

export function AttachmentsDataTable({ policyId }: Props) {
  const policyQuery = usePolicy(policyId);
  const groupsQuery = useGroups();
  const policy = policyQuery.data;
  const groups = groupsQuery.data;

  const { environments } = useEnvironmentList({
    search: '',
    excludeSnapshots: true,
    policy: true,
  });

  const parentFilteredStatusRef = useRef<Map<string, boolean>>(new Map());

  const attachedGroups = groups?.filter(
    (group) => policy?.EnvironmentGroups?.includes(group.Id)
  );

  const environmentsByGroup = useMemo(
    () =>
      attachedGroups?.map((group) => ({
        ...group,
        environments: environments?.filter((env) => env.GroupId === group.Id),
      })),
    [attachedGroups, environments]
  );

  const groupColumns = useMemo(
    () => getGroupColumns(policy?.Type),
    [policy?.Type]
  );
  const envColumns = useMemo(
    () => getEnvironmentColumns(policy?.Type, policy?.EnvironmentType),
    [policy?.Type, policy?.EnvironmentType]
  );

  const tableState = useTableState(settingsStore, storageKey);
  const isLoading = policyQuery.isLoading || groupsQuery.isLoading;

  if (policyQuery.error || groupsQuery.error) {
    return (
      <Alert color="error" title="Error loading policy or groups">
        {(policyQuery.error as Error)?.message ??
          (groupsQuery.error as Error)?.message ??
          'Unknown error'}
      </Alert>
    );
  }

  return (
    <ExpandableDatatable
      dataset={environmentsByGroup || []}
      columns={groupColumns}
      settingsManager={tableState}
      disableSelect
      emptyContentLabel="No environment groups attached to this policy"
      title="Attachments"
      titleIcon={LinkIcon}
      getRowId={(row) => String(row.Id)}
      getRowCanExpand={({ original: group }) => group.environments.length > 0}
      data-cy="policy-attachments-table"
      isLoading={isLoading}
      includeSearch
      renderTableSettings={() => null}
      renderSubRow={({ original: group }) => (
        <tr className="hover:!bg-inherit">
          <td />
          <td colSpan={Number.MAX_SAFE_INTEGER}>
            <EnvironmentsSubTable
              environments={group.environments}
              search={
                parentFilteredStatusRef.current.get(String(group.Id))
                  ? ''
                  : tableState.search
              }
              columns={envColumns}
            />
          </td>
        </tr>
      )}
      extendTableOptions={mergeOptions(
        (options) => ({
          ...options,
          onExpandedChange: (updater) => {
            const value =
              typeof updater === 'function'
                ? updater(tableState.expanded)
                : updater;
            tableState.setExpanded(value);
          },
          state: {
            expanded: tableState.expanded,
          },
        }),
        withGlobalFilter(nestedSearchFilter)
      )}
    />
  );

  function nestedSearchFilter(
    row: Row<AttachmentRowData>,
    columnId: string,
    filterValue: null | { search: string }
  ) {
    const parentMatches = defaultGlobalFilterFn(row, columnId, filterValue);

    const groupId = String(row.original.Id);
    const environmentsMatch = row.original.environments.some((env) =>
      Object.values(env).some(
        (value) =>
          value &&
          value
            .toString()
            .toLowerCase()
            .includes(filterValue?.search?.toLowerCase() || '')
      )
    );

    const matches = parentMatches || environmentsMatch;
    parentFilteredStatusRef.current.set(groupId, parentMatches);

    return matches;
  }
}

interface EnvironmentsSubTableProps {
  environments?: EnvironmentInfo[];
  search?: string;
  columns: ReturnType<typeof getEnvironmentColumns>;
}

function EnvironmentsSubTable({
  environments = [],
  search = '',
  columns,
}: EnvironmentsSubTableProps) {
  return (
    <NestedDatatable
      columns={columns}
      dataset={environments}
      emptyContentLabel="No environments in this group"
      getRowId={(env) => String(env.Id)}
      enablePagination={false}
      search={search}
      data-cy="environments-sub-table"
    />
  );
}
